import { cn } from '@dialogueconsulting/sked-ui/lib/utils'
import type { PropsWithChildren } from 'react'

import {
  CarouselIcon,
  ReelIcon,
  VideoIcon,
  StoryIcon,
  TextIcon,
  UnknownPlatformLogo,
  PlaceholderPhone,
  RejectIcon,
} from 'Icons'
import { type PostType } from 'Types'

import ImageWithFallback from './Common/ImageWithFallback'

export interface PostPreviewGridProps {
  account: {
    name: string
    handler_name: string
    avatar: string
  }
  posts: {
    id: string
    status: string | 'posted' | 'publishing'
    media: {
      type: PostType
      src: string
    }
  }[]
  isHighlightFirstGridItem?: boolean
  isReadOnly: boolean
  scrollableGrid?: boolean
}

// Status corner-markers — exported because the mobile grid (MobilePreviewGrid)
// reuses them. Colours follow the token palette (yellow-500 / green-500 /
// red-500), consistent with the calendar status icons.
export const MediaIconWrap = ({ children }: PropsWithChildren) => (
  <div className="absolute right-0 top-0 w-[25px] p-[5px]">{children}</div>
)

export const IsPendingMarker = () => (
  <UnknownPlatformLogo className="absolute bottom-0 right-0 z-[2] w-[25px] rounded-none rounded-tl-[5px] [&_path]:fill-neutral-800 [&_rect]:fill-yellow-500" />
)

// In-flight (queued/posting) posts. Neutral by design — this is a status
// readout, not an approval decision, so it must not borrow the approved/
// rejected palette. Spans the cell foot so the "Publishing…" copy stays legible
// at grid scale.
export const IsPublishingMarker = () => (
  <div className="absolute bottom-0 left-0 right-0 z-[2] bg-neutral-800/70 px-[5px] py-[2px] text-center text-[9px] font-medium leading-tight text-white">
    Publishing…
  </div>
)

export const IsApprovedMarker = ({ children }: PropsWithChildren) => (
  <div className="absolute bottom-0 right-0 z-[2] grid size-[25px] place-content-center rounded-tl-[5px] bg-green-500 text-[12px] text-white [&_svg]:w-[14px] [&_svg_path]:fill-white">
    {children}
  </div>
)

const IsRejectedMarker = ({ children }: PropsWithChildren) => (
  <div className="absolute bottom-0 right-0 z-[2] grid size-[25px] place-content-center rounded-tl-[5px] bg-red-500 text-white [&_svg]:w-[21px] [&_svg_path]:fill-white [&_svg_rect]:fill-transparent">
    {children}
  </div>
)

export const renderPostTypeIcon = (type: PostType) => {
  switch (type) {
    case 'carousel':
      return <CarouselIcon />
    case 'singleImage':
      return null
    case 'video':
      return <VideoIcon />
    case 'reel':
      return <ReelIcon />
    case 'story':
      return <StoryIcon />
    case 'text':
      return <TextIcon data-testid="text_icon" />
  }
}

const PostPreviewGrid: React.FC<
  PostPreviewGridProps & React.HTMLAttributes<HTMLDivElement>
> = ({
  account,
  posts,
  isHighlightFirstGridItem,
  className,
  isReadOnly,
  scrollableGrid,
}) => {
  return (
    <div
      className={cn('relative max-md:ml-[53px] max-md:mt-[22px]', className)}
    >
      <PlaceholderPhone className="phone-bg absolute top-0 w-[400px] min-w-[400px]" />
      <div className="container" data-testid="preview-grid-container">
        <div className="mx-auto flex w-[295px] max-w-full items-center justify-center text-xs font-bold">
          <ImageWithFallback
            variant="avatar"
            fallbackLabel={account.name}
            src={account.avatar}
            alt={account.handler_name}
            className="mr-2 size-[38px] rounded-full"
          />
          <div className="min-w-0">
            <p className="m-0 truncate" title={account.name}>
              {account.name}
            </p>
            <p
              className="m-0 truncate font-normal leading-[10px]"
              title={account.handler_name}
            >
              {account.handler_name}
            </p>
          </div>
        </div>
        <div
          className={cn(
            'grid-container mx-auto mt-4 grid max-h-[387px] w-[295px] grid-cols-3 gap-[5px]',
            scrollableGrid ? 'overflow-auto' : 'overflow-hidden'
          )}
        >
          {posts.map((post, index) => {
            const isFirst = index === 0
            return (
              <div
                key={post.id}
                className={cn(
                  'relative aspect-[4/5]',
                  '[&>img]:absolute [&>img]:inset-0 [&>img]:size-full [&>img]:object-cover',
                  '[&:first-child]:rounded-tl-[10px] [&:first-child>img]:rounded-tl-[6px]',
                  '[&:nth-child(3)]:rounded-tr-[10px] [&:nth-child(3)>img]:rounded-tr-[10px]',
                  '[&:nth-child(7)]:rounded-bl-[10px] [&:nth-child(7)>img]:rounded-bl-[10px]',
                  '[&:nth-child(10)]:rounded-bl-[10px] [&:nth-child(10)>img]:rounded-bl-[10px]',
                  isFirst && 'first-grid-item',
                  isFirst &&
                    isHighlightFirstGridItem &&
                    'border-4 border-blue-500',
                  (post.status === 'posted' || post.status === 'publishing') &&
                    'opacity-70'
                )}
              >
                <ImageWithFallback
                  variant="media"
                  src={post.media.src}
                  alt={post.media.type}
                  fallbackClassName="absolute inset-0 size-full object-cover"
                />
                <MediaIconWrap>
                  {renderPostTypeIcon(post.media.type)}
                </MediaIconWrap>
                {post.status === 'publishing' && <IsPublishingMarker />}
                {!isReadOnly && (
                  <>
                    {post.status === 'pending' && <IsPendingMarker />}
                    {post.status === 'approved' && (
                      <IsApprovedMarker>
                        <i className="fa-solid fa-check" aria-hidden="true" />
                      </IsApprovedMarker>
                    )}
                    {post.status === 'requestChange' && (
                      <IsRejectedMarker>
                        <RejectIcon />
                      </IsRejectedMarker>
                    )}
                  </>
                )}
              </div>
            )
          })}
        </div>
      </div>
    </div>
  )
}

export default PostPreviewGrid
